import { useMemo } from 'react'

import { useActivateFilter } from '../../hooks/useActivateFilter'
import { useDeleteFilter } from '../../hooks/useDeleteFilter'
import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'
import {
  ActionMenu,
  Box,
  FeedItem,
  FilterIcon,
  Flex,
  KebabVerticalIcon,
  Spinner,
  Text,
  UtilityButton,
} from '@hivewatch/dark'

import { useSavedIncidentsFiltersQuery } from '@/graphql/generated/hooks'

import { SavedFilterI } from '../../types/generic'
import { compareAlphabetically } from '../../utils/utils'
import { DarkDeleteFilterConfirmationModal } from './DarkDeleteFilterConfirmationModal'

interface DarkIncidentsFilterListIProps {
  onFilterUpdate: (filter: SavedFilterI) => void
  colorScheme: ColorScheme
}

export const DarkIncidentsFilterList = ({
  onFilterUpdate,
  colorScheme,
}: DarkIncidentsFilterListIProps) => {
  const { t } = useTranslation()
  const { data, loading: isLoading } = useSavedIncidentsFiltersQuery({
    fetchPolicy: 'network-only',
  })

  const savedFilters = useMemo(() => {
    return (
      data?.savedFilters?.edges
        ?.map((e) => e.node)
        .sort(compareAlphabetically) || []
    )
  }, [data])

  const {
    deleteFilter,
    filter,
    isDeleteLoading,
    isDeleteModalOpen,
    onConfirmFilterDelete,
    onDeleteModalClose,
  } = useDeleteFilter()

  const {
    onActivateFilter,
    onDeactivateFilter,
    isActivateLoading,
    isDeactivateLoading,
  } = useActivateFilter()

  const dangerColor =
    colorScheme === 'light' ? 'light.icon.danger' : 'interactive.danger'

  if (isLoading || isActivateLoading || isDeactivateLoading) {
    return (
      <Flex justifyContent='center' py='40px'>
        <Spinner />
      </Flex>
    )
  }

  return (
    <Box>
      {savedFilters.length > 0 ? (
        <Flex direction='column' gap='8px'>
          {savedFilters.map((savedFilter) => (
            <FeedItem
              align='center'
              colorScheme={colorScheme}
              icon={<FilterIcon colorScheme={colorScheme} />}
              key={savedFilter.id}
              // Saved Filter Row spec: the name is text/primary — brighter
              // than FeedItem's default text/secondary message treatment.
              message={
                <Text
                  color={
                    colorScheme === 'light'
                      ? 'light.text.primary'
                      : 'text.primary'
                  }
                >
                  {savedFilter.name}
                </Text>
              }
              trailingAction={
                <ActionMenu
                  anchorRight
                  colorScheme={colorScheme}
                  items={[
                    savedFilter.isActive
                      ? {
                          label: t('incidents:filter_deactivate'),
                          onClick: () => {
                            void onDeactivateFilter(savedFilter)
                          },
                        }
                      : {
                          label: t('incidents:filter_activate'),
                          onClick: () => {
                            void onActivateFilter(savedFilter)
                          },
                        },
                    {
                      label: t('incidents:filter_update_menu'),
                      onClick: () => onFilterUpdate(savedFilter),
                    },
                    {
                      color: dangerColor,
                      label: t('common:delete'),
                      onClick: () => onConfirmFilterDelete(savedFilter),
                    },
                  ]}
                  trigger={({ onClick }) => (
                    <UtilityButton
                      colorScheme={colorScheme}
                      icon={<KebabVerticalIcon />}
                      label={t('incidents:filter_row_actions')}
                      onClick={onClick}
                    />
                  )}
                />
              }
            >
              {savedFilter.isActive && (
                <Text textStyle='small-body'>
                  {t('incidents:filter_active_badge')}
                </Text>
              )}
            </FeedItem>
          ))}
        </Flex>
      ) : (
        <Text
          color={
            colorScheme === 'light' ? 'light.text.secondary' : 'text.secondary'
          }
          textAlign='center'
          textStyle='caption'
        >
          {t('incidents:filter_no_saved')}
        </Text>
      )}
      {isDeleteModalOpen && (
        <DarkDeleteFilterConfirmationModal
          colorScheme={colorScheme}
          filterName={filter?.name ?? ''}
          isLoading={isDeleteLoading}
          isOpen={isDeleteModalOpen}
          onClose={onDeleteModalClose}
          onConfirm={deleteFilter}
        />
      )}
    </Box>
  )
}
